import { z } from 'zod';
export const uuidSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, 'expected a lowercase RFC 4122 UUID');
export const sha256Schema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'expected a lowercase SHA-256 digest');
export const gitCommitShaSchema = z
    .string()
    .regex(/^[0-9a-f]{40}$/, 'expected a full lowercase Git commit SHA');
export const semverSchema = z
    .string()
    .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/, 'expected a semantic version');
export const calendarDateSchema = z.iso.date();
/**
 * Canonical wire instant. Millisecond precision is intentional: it round-trips
 * through IndexedDB, JSON, PostgreSQL and browser Date without silently
 * inventing or discarding precision.
 */
export const instantSchema = z.iso.datetime({ offset: true, precision: 3 });
export const utcOffsetSchema = z
    .string()
    .regex(/^(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/)
    .refine((value) => value === 'Z' || !value.startsWith('+14:') || value === '+14:00', 'UTC offset beyond +14:00 is invalid')
    .refine((value) => value === 'Z' || !value.startsWith('-14:') || value === '-14:00', 'UTC offset beyond -14:00 is invalid');
export const ianaTimeZoneSchema = z
    .string()
    .min(3)
    .max(64)
    .regex(/^[A-Za-z_+-]+(?:\/[A-Za-z0-9_+-]+)*$/, 'expected an IANA time-zone name')
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0);
        return true;
    }
    catch {
        return false;
    }
}, 'unknown IANA time-zone name');
export const zonedMomentSchema = z
    .strictObject({
    instant: instantSchema,
    time_zone: ianaTimeZoneSchema,
    utc_offset: utcOffsetSchema,
})
    .superRefine((value, context) => {
    const suffix = value.instant.match(/(Z|[+-]\d{2}:\d{2})$/)?.[1];
    if (suffix === undefined ||
        utcOffsetToMinutes(suffix) !== utcOffsetToMinutes(value.utc_offset)) {
        context.addIssue({
            code: 'custom',
            path: ['utc_offset'],
            message: 'utc_offset must equal the offset encoded in instant',
        });
        return;
    }
    const expectedOffset = offsetForTimeZone(value.instant, value.time_zone);
    if (expectedOffset !== utcOffsetToMinutes(value.utc_offset)) {
        context.addIssue({
            code: 'custom',
            path: ['time_zone'],
            message: 'time_zone offset does not match instant and utc_offset',
        });
    }
});
export const revisionSchema = z.number().int().safe().nonnegative();
export const positiveSafeIntegerSchema = z.number().int().safe().positive();
export const nonnegativeSafeIntegerSchema = z.number().int().safe().nonnegative();
export const metresSchema = z.number().int().safe().nonnegative();
/**
 * Recorded mileage is deliberately signed. The product warns about negative
 * or extreme user/source values but keeps them auditable instead of silently
 * refusing or rewriting them. Physical graph-edge lengths still use
 * `metresSchema` and therefore remain non-negative.
 */
export const recordedMetresSchema = z.number().int().safe();
export const latitudeSchema = z.number().finite().min(-90).max(90);
export const longitudeSchema = z.number().finite().min(-180).max(180);
export const decimalCursorSchema = z
    .string()
    .regex(/^(?:0|[1-9]\d*)$/, 'expected a canonical unsigned decimal cursor');
export const opaqueSubjectSchema = z
    .string()
    .min(1)
    .max(512)
    .regex(/^[\x21-\x7e]+$/, 'subject must be a non-whitespace opaque ASCII value');
export const nonEmptyTextSchema = z
    .string()
    .min(1)
    .max(512)
    .refine((value) => value.trim() === value, 'leading or trailing whitespace is not canonical');
export const noteSchema = z.string().max(10_000);
export const countryCodeSchema = z
    .string()
    .regex(/^[A-Z]{2}$/, 'expected an ISO 3166-1 alpha-2 code');
export const relativeUrlPathSchema = z
    .string()
    .min(1)
    .max(2_048)
    .regex(/^\/(?!\/)/, 'expected an absolute same-origin path')
    .regex(/^\/(?:[A-Za-z0-9._~-]+\/)*[A-Za-z0-9._~-]*$/, 'path must use only canonical ASCII URL-path characters')
    .refine((value) => {
    try {
        const parsed = new URL(value, 'https://contract.invalid');
        return (parsed.origin === 'https://contract.invalid' &&
            !parsed.username &&
            !parsed.password &&
            parsed.search === '' &&
            parsed.hash === '' &&
            parsed.pathname === value &&
            !value.includes('\\') &&
            !value.includes('%') &&
            !value.includes('//') &&
            !value.split('/').some((segment) => segment === '.' || segment === '..'));
    }
    catch {
        return false;
    }
}, 'expected a canonical same-origin path without aliases, query, or fragment');
export const jsonValueSchema = z.custom((value) => isJsonValue(value, new Set()), 'expected a finite, acyclic JSON value without hidden properties');
function isJsonValue(value, ancestors) {
    if (value === null ||
        typeof value === 'boolean' ||
        typeof value === 'string' ||
        (typeof value === 'number' && Number.isFinite(value))) {
        return true;
    }
    if (typeof value !== 'object' || ancestors.has(value)) {
        return false;
    }
    ancestors.add(value);
    let valid;
    if (Array.isArray(value)) {
        const ownNames = Object.getOwnPropertyNames(value);
        valid =
            Object.getOwnPropertySymbols(value).length === 0 &&
                ownNames.length === value.length + 1 &&
                ownNames.every((key) => key === 'length' || (/^(?:0|[1-9]\d*)$/.test(key) && Number(key) < value.length)) &&
                Array.from({ length: value.length }, (_, index) => index).every((index) => {
                    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
                    return (descriptor !== undefined &&
                        descriptor.enumerable &&
                        'value' in descriptor &&
                        isJsonValue(descriptor.value, ancestors));
                });
    }
    else {
        const prototype = Object.getPrototypeOf(value);
        const ownNames = Object.getOwnPropertyNames(value);
        valid =
            (prototype === Object.prototype || prototype === null) &&
                Object.getOwnPropertySymbols(value).length === 0 &&
                ownNames.every((key) => {
                    const descriptor = Object.getOwnPropertyDescriptor(value, key);
                    return (descriptor !== undefined &&
                        descriptor.enumerable &&
                        'value' in descriptor &&
                        isJsonValue(descriptor.value, ancestors));
                });
    }
    ancestors.delete(value);
    return valid;
}
function canonicalizeJson(value) {
    if (Array.isArray(value)) {
        return value.map(canonicalizeJson);
    }
    if (value !== null && typeof value === 'object') {
        const canonical = Object.create(null);
        for (const key of Object.keys(value).sort()) {
            canonical[key] = canonicalizeJson(value[key]);
        }
        return canonical;
    }
    return Object.is(value, -0) ? 0 : value;
}
/**
 * Project canonical JSON v1: validated JSON values, lexicographically sorted
 * object keys, preserved array order, no whitespace and -0 normalised to 0.
 */
export function canonicalJsonStringify(input) {
    const value = jsonValueSchema.parse(input);
    return JSON.stringify(canonicalizeJson(value));
}
/**
 * Dependency-free SHA-256 over UTF-8 text. This deliberately exposes one
 * deterministic implementation to browser workers and Node instead of making
 * contract validation depend on an environment-specific crypto adapter.
 */
export function sha256HexUtf8(input) {
    return sha256HexBytes(new TextEncoder().encode(input));
}
export function sha256HexBytes(bytesInput) {
    const bytes = new Uint8Array(bytesInput.buffer, bytesInput.byteOffset, bytesInput.byteLength);
    const bitLength = bytes.length * 8;
    const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
    const padded = new Uint8Array(paddedLength);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const paddedView = new DataView(padded.buffer);
    paddedView.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000), false);
    paddedView.setUint32(paddedLength - 4, bitLength >>> 0, false);
    const state = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const schedule = new Uint32Array(64);
    for (let offset = 0; offset < padded.length; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            schedule[index] = paddedView.getUint32(offset + index * 4, false);
        }
        for (let index = 16; index < 64; index += 1) {
            const previous15 = schedule[index - 15] ?? 0;
            const previous2 = schedule[index - 2] ?? 0;
            const sigma0 = rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
            const sigma1 = rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
            schedule[index] =
                ((schedule[index - 16] ?? 0) + sigma0 + (schedule[index - 7] ?? 0) + sigma1) >>> 0;
        }
        let a = state[0] ?? 0;
        let b = state[1] ?? 0;
        let c = state[2] ?? 0;
        let d = state[3] ?? 0;
        let e = state[4] ?? 0;
        let f = state[5] ?? 0;
        let g = state[6] ?? 0;
        let h = state[7] ?? 0;
        for (let index = 0; index < 64; index += 1) {
            const bigSigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temporary1 = (h + bigSigma1 + choose + (SHA256_ROUND_CONSTANTS[index] ?? 0) + (schedule[index] ?? 0)) >>>
                0;
            const bigSigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temporary2 = (bigSigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temporary1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temporary1 + temporary2) >>> 0;
        }
        state[0] = ((state[0] ?? 0) + a) >>> 0;
        state[1] = ((state[1] ?? 0) + b) >>> 0;
        state[2] = ((state[2] ?? 0) + c) >>> 0;
        state[3] = ((state[3] ?? 0) + d) >>> 0;
        state[4] = ((state[4] ?? 0) + e) >>> 0;
        state[5] = ((state[5] ?? 0) + f) >>> 0;
        state[6] = ((state[6] ?? 0) + g) >>> 0;
        state[7] = ((state[7] ?? 0) + h) >>> 0;
    }
    return [...state].map((word) => word.toString(16).padStart(8, '0')).join('');
}
export const digestContractSchema = z.enum([
    'dmf.export-payload.sha256.v1',
    'dmf.route-variant-identity.sha256.v1',
    'dmf.account-identity.sha256.v1',
]);
export function sha256CanonicalJson(contractInput, input) {
    const digestContract = digestContractSchema.parse(contractInput);
    return sha256HexUtf8(canonicalJsonStringify({
        digest_contract: digestContract,
        value: jsonValueSchema.parse(input),
    }));
}
const SHA256_ROUND_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function rotateRight(value, count) {
    return (value >>> count) | (value << (32 - count));
}
export function compareInstants(left, right) {
    return Date.parse(left) - Date.parse(right);
}
export function compareDecimalCursors(left, right) {
    const parsedLeft = decimalCursorSchema.parse(left);
    const parsedRight = decimalCursorSchema.parse(right);
    const leftValue = BigInt(parsedLeft);
    const rightValue = BigInt(parsedRight);
    return leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
}
export function utcOffsetToMinutes(offset) {
    const parsed = utcOffsetSchema.parse(offset);
    if (parsed === 'Z') {
        return 0;
    }
    const sign = parsed.startsWith('-') ? -1 : 1;
    const [hours, minutes] = parsed.slice(1).split(':').map(Number);
    return sign * ((hours ?? 0) * 60 + (minutes ?? 0));
}
function offsetForTimeZone(instant, timeZone) {
    const epoch = Date.parse(instant);
    const formatter = new Intl.DateTimeFormat('en-CA', {
        calendar: 'gregory',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
        minute: '2-digit',
        month: '2-digit',
        second: '2-digit',
        timeZone,
        year: 'numeric',
    });
    const parts = Object.fromEntries(formatter
        .formatToParts(epoch)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]));
    const representedAsUtc = Date.UTC(parts.year ?? 0, (parts.month ?? 1) - 1, parts.day ?? 1, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0);
    return Math.round((representedAsUtc - Math.floor(epoch / 1_000) * 1_000) / 60_000);
}
//# sourceMappingURL=primitives.js.map